import type { ButtonHTMLAttributes, DetailedHTMLProps, Ref } from 'react';
import { Link, type LinkProps } from 'react-router';
import type { FieldValues, FormState } from 'react-hook-form';
import { cn } from '@dinewise/utils';
import { CheckIcon, PencilSquareIcon, XCircleIcon } from '@heroicons/react/24/outline';
import { useField, useFormContext, useIsSubmitting } from '@michaelgmcd/rvf';

type HtmlButtonProps = DetailedHTMLProps<
  ButtonHTMLAttributes<HTMLButtonElement>,
  HTMLButtonElement
>;

export type ButtonTheme =
  | 'primary'
  | 'secondary'
  | 'beige'
  | 'gray'
  | 'transparent'
  | 'red'
  | 'link'
  | 'ande-mint'
  | 'ande-mint-outline';

type ButtonSize = 'xxs' | 'xs' | 'sm' | 'md' | 'lg' | 'xl';

type ThemeProps = {
  theme: ButtonTheme;
  size: ButtonSize;
};

export type ButtonProps = HtmlButtonProps & ThemeProps;
export type LinkButtonProps = LinkProps &
  ThemeProps & {
    disabled?: boolean;
  };

type GetButtonClassNamesParams = {
  theme: ButtonTheme;
  size: ButtonSize;
  disabled?: boolean;
  overrides?: string;
};

export const getButtonClassNames = ({
  theme,
  size,
  disabled,
  overrides,
}: GetButtonClassNamesParams) =>
  cn(
    'flex items-center justify-center whitespace-nowrap rounded-lg border font-medium shadow-sm focus:outline-none focus:ring-2 focus:ring-ande-black focus:ring-offset-2',
    !disabled && theme === 'primary' && 'bg-ande-black text-ande-white hover:bg-ande-gray-500',
    disabled && theme === 'primary' && 'border-none bg-ande-gray-100 text-ande-gray-300',
    !disabled &&
      theme === 'ande-mint' &&
      'border-none bg-ande-mint text-ande-black hover:bg-ande-mint-dark',
    disabled && theme === 'ande-mint' && 'border-none bg-ande-gray-100 text-ande-gray-300',
    !disabled &&
      theme === 'ande-mint-outline' &&
      'border border-ande-mint bg-transparent text-ande-mint hover:border-ande-mint-dark hover:text-ande-mint-dark focus:ring-ande-mint',
    disabled &&
      theme === 'ande-mint-outline' &&
      'border-none bg-ande-gray-300 text-ande-surface-500',
    !disabled &&
      theme === 'secondary' &&
      'border-none bg-ande-white text-ande-black ring-1 ring-ande-black ring-inset hover:bg-ande-gray-100 focus:ring-ande-white',
    disabled && theme === 'secondary' && 'border-none bg-ande-white text-ande-gray-300',
    !disabled &&
      theme === 'beige' &&
      'border-none bg-ande-surface-200 text-ande-black hover:bg-ande-surface-300',
    disabled && theme === 'beige' && 'border-none bg-ande-gray-100 text-ande-gray-300',
    !disabled &&
      theme === 'gray' &&
      'border border-ande-gray-200 bg-ande-white text-ande-black hover:bg-ande-gray-200',
    disabled && theme === 'gray' && 'border-none bg-ande-white text-ande-gray-300',
    !disabled &&
      theme === 'red' &&
      'border-ande-error-light bg-ande-error-light text-ande-black hover:bg-ande-error-lighter',
    disabled &&
      theme === 'red' &&
      'border-ande-error-light border-none bg-ande-error-light text-ande-black',
    !disabled &&
      theme === 'transparent' &&
      'bg-transparent text-ande-black shadow-none focus:ring-none',
    disabled && theme === 'transparent' && 'bg-transparent text-ande-gray-300 shadow-none',
    !disabled &&
      theme === 'link' &&
      'border-none text-ande-black text-semibold underline underline-offset-4 focus:ring-none',
    disabled &&
      theme === 'link' &&
      'border-none border-none text-ande-gray-300 text-semibold underline underline-offset-4',
    size === 'xs' && 'px-2.5 py-1.5 text-xs',
    size === 'sm' && 'px-3 py-2 text-xs leading-4',
    size === 'md' && 'rounded-2xl px-4 py-3 font-medium text-sm leading-none',
    size === 'lg' && 'px-4 py-2 text-base',
    size === 'xl' && 'px-6 py-3 text-base',
    overrides,
  );

export const Button = ({ size, theme, disabled, ...rest }: ButtonProps) => {
  return (
    <button
      {...rest}
      {...{ disabled }}
      className={getButtonClassNames({ theme, size, disabled, overrides: rest.className })}
    >
      {rest.children}
    </button>
  );
};

export const LinkButton = ({ size, theme, disabled, ...rest }: LinkButtonProps) => {
  return (
    <Link
      prefetch="intent"
      onClick={e => {
        if (disabled) {
          e.preventDefault();
        }
      }}
      {...rest}
      className={getButtonClassNames({
        theme,
        size,
        disabled,
        overrides: cn(rest.className, theme === 'link' ? 'px-0 py-0' : null),
      })}
    />
  );
};

export const EditButton = ({ ...rest }) => {
  return (
    <SubmitButtonWithoutName size="sm" theme="primary" {...rest}>
      <PencilSquareIcon className="size-5" />
    </SubmitButtonWithoutName>
  );
};

export const CheckButton = ({ ...rest }) => {
  return (
    <SubmitButtonWithoutName size="sm" theme="primary" {...rest}>
      <CheckIcon className="size-5" />
    </SubmitButtonWithoutName>
  );
};

export const CancelButton = ({ ...rest }) => {
  return (
    <SubmitButtonWithoutName size="sm" theme="red" {...rest}>
      <XCircleIcon className="size-5" />
    </SubmitButtonWithoutName>
  );
};

export type SubmitButtonProps = {
  label?: string;
  submittingLabel?: string;
  disabled?: boolean;
  ref?: Ref<HTMLButtonElement>;
} & HtmlButtonProps &
  Partial<ThemeProps>;

const SubmitButtonWithoutName = ({
  label,
  submittingLabel,
  disabled,
  ref,
  ...buttonProps
}: SubmitButtonProps) => {
  const { form } = buttonProps;
  const isSubmitting = useIsSubmitting(form);
  const { isValid } = useFormContext(form);
  const defaultIsDisabled = !!disabled || isSubmitting || !isValid;
  const defaultLabel = isSubmitting ? (submittingLabel ?? label) : label;
  const children = isSubmitting && submittingLabel ? submittingLabel : buttonProps.children;

  return (
    <Button
      size="md"
      theme="primary"
      disabled={defaultIsDisabled}
      {...{ ref }}
      {...buttonProps}
      type="submit"
    >
      {children ?? defaultLabel}
    </Button>
  );
};

const SubmitButtonWithName = ({ name, ...rest }: SubmitButtonProps & { name: string }) => {
  const { getInputProps } = useField(name);

  return <SubmitButtonWithoutName {...getInputProps(rest)} />;
};

export const SubmitButton = ({ name, ...rest }: SubmitButtonProps) => {
  if (name) {
    return <SubmitButtonWithName {...rest} {...{ name }} />;
  }

  return <SubmitButtonWithoutName {...rest} />;
};

export type RHFSubmitButtonProps = SubmitButtonProps & {
  formState: FormState<FieldValues>;
  allowInvalidSubmit?: boolean;
};

export const RHFSubmitButton = ({
  ref,
  label,
  submittingLabel,
  disabled,
  formState,
  allowInvalidSubmit,
  ...buttonProps
}: RHFSubmitButtonProps) => {
  const { isSubmitting, isValid } = formState;
  const shouldDisableBasedOnValidity = !allowInvalidSubmit && !isValid;
  const defaultIsDisabled = !!disabled || isSubmitting || shouldDisableBasedOnValidity;
  const defaultLabel = isSubmitting ? (submittingLabel ?? label) : label;
  const children = isSubmitting && submittingLabel ? submittingLabel : buttonProps.children;

  return (
    <Button
      {...{ ref }}
      size="md"
      theme="primary"
      disabled={defaultIsDisabled}
      {...buttonProps}
      type="submit"
    >
      {children ?? defaultLabel}
    </Button>
  );
};
